<template>
  <WdsStack v-if="!loading && endDate !== ''" direction-column class="tw-px-2">
    <WdsStack justify-space-between>
      <WdsText>
        {{ $t('Contract ended on :date', { date: endDate }) }}
      </WdsText>
    </WdsStack>
    <WdsText secondary>
      <div class="tw-font-normal">
        {{
          (isWorker || (isRecruiter && hasRecruiterManagingWorkers))
            ? $t('You can still submit payment requests within the contract timeframe.')
            : hasRecruiterManagingWorkers
              ? $t('The staffing agency can still submit payment requests within the contract timeframe.')
              : $t('The worker can still submit payment requests within the contract timeframe.')
        }}
      </div>
    </WdsText>
  </WdsStack>

  <PendingContractAcceptanceAlert :hire-id="hireId" class="tw-px-2 tw-mb-2" />

  <HireComplianceGateStatus :compliances="active" :loading="complianceLoading" :hire-id="hireId">
    <template #hire-actions>
      <FeatureFlagged v-if="hasUnapprovedPaymentRequests" name="hire-page-payment-requests-tab">
        <HireAlert
          title="Payment request pending"
          :icon="WdsIconNameEnum.RECEIPT"
          button-text="Review"
          status="warning"
          @click="router.push(`/hires/${hireId}/payments`)"
        />
        <template #is-disabled>
          <HireAlert
            title="Payment request pending"
            :icon="WdsIconNameEnum.RECEIPT"
            :href="`${laravelNamedRoute('billing.fmsBills')}?bill-status=UNAPPROVED`"
            button-text="Review"
            status="warning"
          />
        </template>
      </FeatureFlagged>

      <HireAlert
        title="Contract has ended"
        description="You can extend the contract, if more time is needed."
        :icon="WdsIconNameEnum.INFO"
        button-text="Extend"
        status="none"
        :action="extendContract"
      />
    </template>
  </HireComplianceGateStatus>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { WdsIconNameEnum, WdsStack, WdsText } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { useHireRecruiter } from '../composables/useHireRecruiter'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useDate from '@/composables/useDate'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import HireAlert from '@/components/Hire/HireAlert.vue'
import PendingContractAcceptanceAlert from '@/components/Hire/PendingContractAcceptanceAlert.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import { useContractDrawer } from '@/components/Hire/composables/useContractDrawer'
import HireComplianceGateStatus from '@/components/Hire/HirePageCompliance/HireComplianceGateStatus.vue'
import { useComplianceGate } from '@/components/Hire/HirePageCompliance/useComplianceGates'
import { ComplianceName } from '@/gql/graphql'

const router = useRouter()

const props = defineProps<{
  hireId: string
}>()

const { openContractDrawer } = useContractDrawer()
const { isWorker, isRecruiter } = useAccountCapabilities()
const { hasRecruiterManagingWorkers } = useHireRecruiter(props.hireId)

const { result, loading } = useQuery(
  graphql(`
    query HireEndedStatus($id: ID!) {
      hire(id: $id) {
        id
        endDate
        paymentRequests(statuses: [UNAPPROVED]) {
          paginatorInfo {
            total
          }
        }
      }
    }
  `),
  {
    id: props.hireId,
  },
  {
    clientId: 'publicApi',
  },
)

const { active, loading: complianceLoading } = useComplianceGate(
  props.hireId,
  ComplianceName.GateHireCreateBill,
)

const hire = computed(() => result.value?.hire)

const endDate = computed(() => {
  if (!hire.value?.endDate) return ''
  return useDate(hire.value.endDate, 'MMM DD, YYYY').date.value
})

const hasUnapprovedPaymentRequests = computed(
  () => (hire.value?.paymentRequests?.paginatorInfo?.total ?? 0) > 0,
)

const extendContract = () => {
  openContractDrawer('edit')
}
</script>
